/* eslint-disable camelcase */
import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import {
  IRegimeEspecial,
  IRegimeEspecialFormPayload,
  IRegimeEspecialKpis,
} from '../types/regimeEspecial';

const REGIMES_BASE = '/api/v1/empresas';

export const useRegimesEspeciais = () => {
  const getRegimesEspeciaisCarteira = async (
    key: QueryFunctionContext,
  ): Promise<IData<IRegimeEspecial[]>> => {
    const [, params] = key.queryKey as [
      string,
      {
        esfera?: string;
        status?: string;
        tipo?: string;
        uf?: string;
        dias_para_expirar?: number;
        search?: string;
        page?: number;
        size?: number;
      },
    ];
    const res = await api.get<IData<IRegimeEspecial[]>>(`${REGIMES_BASE}/regimes-especiais/`, {
      params,
    });
    return res.data;
  };

  const getRegimesEspeciaisKpis = async (
    key: QueryFunctionContext,
  ): Promise<IRegimeEspecialKpis> => {
    const [, params] = key.queryKey as [string, { esfera?: string }];
    const res = await api.get<IDataNoPagination<IRegimeEspecialKpis>>(
      `${REGIMES_BASE}/regimes-especiais/kpis/`,
      { params },
    );
    return res.data.data;
  };

  const getRegimesEspeciaisEmpresa = async (
    key: QueryFunctionContext,
  ): Promise<IRegimeEspecial[]> => {
    const [, params] = key.queryKey as [
      string,
      { empresa_id: number; esfera?: string; status?: string; tipo?: string; vigentes?: boolean },
    ];
    const { empresa_id, ...filters } = params;
    const res = await api.get<IData<IRegimeEspecial[]> | IDataNoPagination<IRegimeEspecial[]>>(
      `${REGIMES_BASE}/${empresa_id}/regimes-especiais/`,
      { params: filters },
    );
    const payload = res.data?.data as IRegimeEspecial[] | { items?: IRegimeEspecial[] } | undefined;
    if (Array.isArray(payload)) return payload;
    return payload?.items ?? [];
  };

  const createRegimeEspecial = async (
    empresa_id: number,
    payload: IRegimeEspecialFormPayload,
  ): Promise<IRegimeEspecial> => {
    const res = await api.post<IDataNoPagination<IRegimeEspecial>>(
      `${REGIMES_BASE}/${empresa_id}/regimes-especiais/`,
      payload,
    );
    return res.data.data;
  };

  const updateRegimeEspecial = async (
    empresa_id: number,
    regime_id: number,
    payload: Partial<IRegimeEspecialFormPayload>,
  ): Promise<IRegimeEspecial> => {
    const res = await api.put<IDataNoPagination<IRegimeEspecial>>(
      `${REGIMES_BASE}/${empresa_id}/regimes-especiais/${regime_id}/`,
      payload,
    );
    return res.data.data;
  };

  const deleteRegimeEspecial = async (empresa_id: number, regime_id: number): Promise<void> => {
    await api.delete(`${REGIMES_BASE}/${empresa_id}/regimes-especiais/${regime_id}/`);
  };

  return {
    getRegimesEspeciaisCarteira,
    getRegimesEspeciaisKpis,
    getRegimesEspeciaisEmpresa,
    createRegimeEspecial,
    updateRegimeEspecial,
    deleteRegimeEspecial,
  };
};
